import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
  useSearchParams
} from 'react-router-dom';
import { useLocalStorage } from 'usehooks-ts';

import { useGetCustomerCurrentUser } from 'api';

import { selectLogoutState } from 'reduxLocal/slices/authSlice';
import { resetState, useAppDispatch } from 'reduxLocal/store';

import type { AuthScope } from 'models/authJwt';

import { useAccessToken } from 'hooks/context';
import { useRedirectTo } from 'hooks/useRedirectTo';

import { Loading } from 'components/pages/Loading';
import { LoggingIn } from 'components/ui/organisms/LoggingIn';

type Props = {
  allowedScopes?: AuthScope[];
  redirectionPath?: string;
};

export const RequireAuth = ({ allowedScopes }: Props) => {
  const location: { state: any; pathname: string; search: string } =
    useLocation();

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useLocalStorage('isLoggedIn', false);
  const logoutState = useSelector(selectLogoutState);

  const { accessToken: authToken, scopes } = useAccessToken();

  const { refetch, isFetching, isSuccess, isError } =
    useGetCustomerCurrentUser();

  const searchParams = useSearchParams()[0];
  const { navigateWithRedirectTo } = useRedirectTo();

  useEffect(() => {
    if ((!authToken && !isLoggedIn) || logoutState || isError) {
      const didUserComeFromDocs = searchParams.get('fromDocs') !== null;
      const didUserLogoutFromApp = searchParams.get('user_logout') !== null;

      if (didUserComeFromDocs) {
        navigate('login?fromDocs=true');
        return;
      }

      setIsLoggedIn(false);
      dispatch(resetState);

      if (!didUserLogoutFromApp) {
        navigateWithRedirectTo(
          'login',
          {
            replace: true,
            state: {
              reason: logoutState
            }
          },
          `${location.pathname}${location.search}`
        );
      } else {
        navigate('logout');
      }
    } else {
      refetch();
    }
  }, [logoutState, isError, isLoggedIn]);

  return (
    <>
      {isFetching &&
        (location.state?.loggingIn ? (
          <LoggingIn />
        ) : (
          <Loading text="Loading Current User" />
        ))}
      {isSuccess &&
        (allowedScopes &&
        !scopes?.find((scope) => allowedScopes?.includes(scope)) ? (
          <Navigate to="/login?redirect_code=MissingScope" replace />
        ) : (
          <Outlet />
        ))}
    </>
  );
};
